import React, { useEffect, useState } from 'react';
import {
	MsalProvider,
	AuthenticatedTemplate,
	UnauthenticatedTemplate,
	useMsal,
} from '@azure/msal-react';
import { InteractionStatus, EventType, EventMessage, AuthenticationResult, PublicClientApplication } from '@azure/msal-browser';
import { isAuthMocked } from '../config/config';
import { useConfigContext } from '../config/configContext';
import { createMsalConfig, loginRequest } from '../msalConfig';
import AuthErrorPage from '../components/AuthErrorPage';

const isMsalConfigValid = (cfg: ReturnType<typeof useConfigContext>['config']['msal']) =>
	cfg.clientId && cfg.authority && cfg.redirectUri;

const MsalAuthenticator: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const { instance, inProgress } = useMsal();
	const { config } = useConfigContext();
	const [authError, setAuthError] = useState<string | null>(null);
	const [loginStarted, setLoginStarted] = useState(false);

	useEffect(() => {
		const callbackId = instance.addEventCallback((event: EventMessage) => {
			if (event.eventType === EventType.LOGIN_FAILURE) {
				setAuthError('Login failed. Please try again.');
			}
			if (event.eventType === EventType.ACQUIRE_TOKEN_FAILURE) {
				setAuthError('Failed to acquire token. Please try again.');
			}
			if (event.eventType === EventType.LOGIN_SUCCESS) {
				const result = event.payload as AuthenticationResult;
				if (result && result.account) {
					instance.setActiveAccount(result.account);
				}
			}
		});

		return () => {
			if (callbackId) {
				instance.removeEventCallback(callbackId);
			}
		};
	}, [instance]);

	useEffect(() => {
		if (inProgress === InteractionStatus.None) {
			const accounts = instance.getAllAccounts();
			if (!isMsalConfigValid(config.msal)) {
				setAuthError('MSAL is not configured (missing authority, clientId, or redirectUri).');
				return;
			}
			if (accounts.length === 0 && !loginStarted) {
				setLoginStarted(true);
				instance
					.loginRedirect(loginRequest(config.msal.scopes))
					.catch((error) => {
						console.error('Login redirect error:', error);
						setAuthError('Failed to initiate login. Please try again.');
						// allow retry if user triggers it manually
						setLoginStarted(false);
					});
			} else if (!instance.getActiveAccount()) {
				instance.setActiveAccount(accounts[0]);
			}
		}
	}, [instance, inProgress, config.msal.scopes, config.msal, loginStarted]);

	if (authError) {
		return <AuthErrorPage />;
	}

	return (
		<>
			<AuthenticatedTemplate>{children}</AuthenticatedTemplate>
			<UnauthenticatedTemplate>
				{inProgress === InteractionStatus.None ? <AuthErrorPage /> : <div />}
			</UnauthenticatedTemplate>
		</>
	);
};

const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const { config } = useConfigContext();
	const [msalInstance, setMsalInstance] = useState<PublicClientApplication | null>(null);
	const [initError, setInitError] = useState(false);

	useEffect(() => {
		if (isAuthMocked) return;

		const initializeMsal = async () => {
			try {
				if (!isMsalConfigValid(config.msal)) {
					console.error('MSAL configuration is missing required fields.');
					setInitError(true);
					return;
				}
				const msalConfig = createMsalConfig(config.msal);
				const instance = new PublicClientApplication(msalConfig);

				// Initialize MSAL instance (required in v3.x)
				await instance.initialize();

				// Handle redirect response
				await instance.handleRedirectPromise();

				setMsalInstance(instance);
			} catch (error) {
				console.error('MSAL initialization error:', error);
				setInitError(true);
			}
		};

		initializeMsal();
	}, [config.msal]);

	if (isAuthMocked) {
		return <>{children}</>;
	}

	if (initError) {
		return <AuthErrorPage />;
	}

	if (!msalInstance) {
		return <div />;
	}

	return (
		<MsalProvider instance={msalInstance}>
			<MsalAuthenticator>{children}</MsalAuthenticator>
		</MsalProvider>
	);
};

export default AuthProvider;
